import { useContext, useMemo } from "react";
import { ModuleBuilderContext } from "@src/features/libraryItem/module/contexts/ModuleBuilderContext";
import { SetActiveClientInput } from "@src/types.generated";
import { ClientIdContext } from "@src/features/builders/shared/presence/ClientIdContext";
import { CourseBuilderContext } from "@src/features/libraryItem/course/contexts/CourseBuilderContext";
import useSkillBuilderStore, {
  useActiveSkillBuilderStepId,
} from "@src/features/libraryItem/skill/hooks/useSkillBuilderStore";
import { useAtomValue } from "jotai";

import useSyncActiveClientCore from "@src/features/builders/shared/presence/useSyncActiveClientCore";

import { moduleVersionIdAtom } from "../module/atoms";
import useCourseBuilderStore, {
  selectActiveScreen,
  selectCourseVersionId,
} from "../course/stores/useCourseBuilderStore";
import { richTextDocumentIdAtom } from "../resource/atoms";

type Opts = { skip?: boolean };

// LibraryItem wrapper: assembles the active-client input from the
// builder contexts (course / module / skill / training resource)
// + the active prompt / membership / step. Delegates the
// mutation/interval/unload orchestration to useSyncActiveClientCore.
const useSyncActiveClient = (opts?: Opts): void => {
  const { skip } = opts || {};
  const courseBuilderContext = useContext(CourseBuilderContext);
  const moduleBuilderContext = useContext(ModuleBuilderContext);
  const skillVersionId = useSkillBuilderStore(
    (state) => state.skillVersion?.id,
  );
  const activeStepId = useActiveSkillBuilderStepId();
  const clientIdContext = useContext(ClientIdContext);

  const hasCourseBuilder = !!courseBuilderContext;
  const promptUuid = useCourseBuilderStore((s) => {
    if (!hasCourseBuilder) return null;
    const activeScreen = selectActiveScreen(s);
    return activeScreen && "promptScreen" in activeScreen
      ? activeScreen.promptScreen.prompt.uuid
      : null;
  });
  const courseVersionId = useCourseBuilderStore(selectCourseVersionId);
  const moduleVersionId = useAtomValue(moduleVersionIdAtom);
  const richTextDocumentId = useAtomValue(richTextDocumentIdAtom);

  const input = useMemo<SetActiveClientInput | null>(() => {
    if (skip || !clientIdContext) return null;
    return {
      clientId: clientIdContext.clientId,
      promptUuid,
      courseVersionId: courseVersionId || null,
      moduleVersionId: moduleVersionId || null,
      moduleContentMembershipUuid:
        moduleBuilderContext?.activeMembershipUuid || null,
      skillVersionId: skillVersionId || null,
      verificationStepId: activeStepId,
      richTextDocumentId: richTextDocumentId,
    } satisfies SetActiveClientInput;
  }, [
    skip,
    clientIdContext,
    promptUuid,
    courseVersionId,
    moduleVersionId,
    moduleBuilderContext?.activeMembershipUuid,
    skillVersionId,
    activeStepId,
    richTextDocumentId,
  ]);

  useSyncActiveClientCore(input);
};

export default useSyncActiveClient;
